import type { ExperienceListItem } from '../types';
import { trackEvent } from '../utils/track';

function formatDuration(duration: string): string {
  if (!duration) return '';
  const parts = duration.split(':').map((n) => parseInt(n, 10));
  const h = parts[0] || 0;
  const m = parts[1] || 0;
  if (!h && !m) return duration;
  if (h && m) return `${h}h${m.toString().padStart(2, '0')}`;
  if (h) return `${h}h`;
  return `${m}min`;
}

// priceUnit do backend chega inconsistente: as vezes "pessoa", as vezes
// "por pessoa". Limpa o prefixo "por " pra a UI sempre poder anexar
// "por {unit}" sem duplicar.
function cleanPriceUnit(unit: string): string {
  return unit.replace(/^por\s+/i, '').trim();
}

// "Audience" no backend == tags categoria "audience" → traduzida para
// "Público" no DTO. A chave real pode variar (com/sem acento, case-
// insensitive, ou ainda "audience" caso o presenter mude), entao normaliza
// e procura. Compoe frase natural "Ideal para X, Y e Z." a partir da lista.
// Sem tags de Publico → string vazia (card volta ao layout so-preco).
function normalizeKey(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
}

function getAudienceTags(tags: Record<string, string[]> | undefined): string[] {
  if (!tags) return [];
  for (const [key, values] of Object.entries(tags)) {
    const n = normalizeKey(key);
    if (n === 'publico' || n === 'audience') {
      return values?.filter((t) => t && t.trim()) ?? [];
    }
  }
  return [];
}

function formatAudience(tags: Record<string, string[]> | undefined): string {
  const items = getAudienceTags(tags);
  if (items.length === 0) return '';
  const lower = items.map((t) => t.toLowerCase().trim());
  let list: string;
  if (lower.length === 1) list = lower[0];
  else if (lower.length === 2) list = `${lower[0]} e ${lower[1]}`;
  else list = `${lower.slice(0, -1).join(', ')} e ${lower[lower.length - 1]}`;
  return `Ideal para ${list}.`;
}

function AudienceIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

const tierClass = (tier: string): 'emalta' | 'exclusivo' | 'descubra' | '' => {
  const t = tier.toLowerCase().trim();
  if (!t) return '';
  if (t.includes('alta')) return 'emalta';
  if (t.includes('exclusiv')) return 'exclusivo';
  return 'descubra';
};

function TierIcon({ tier }: { tier: 'emalta' | 'exclusivo' | 'descubra' }) {
  if (tier === 'emalta') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
      </svg>
    );
  }
  if (tier === 'exclusivo') {
    return (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z" />
      </svg>
    );
  }
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

interface ExperienceCardProps {
  exp: ExperienceListItem;
}

/**
 * Card de experiencia (home).
 *
 * Layout:
 *   - Imagem unica (sem carrossel) com tier badge (top-left) e booking-type
 *     badge (top-right).
 *   - Body: cidade (chip verde), titulo, summary, quick-info, preco alinhado
 *     a direita.
 *   - Sem botoes — o card inteiro eh clicavel (stretched link) e leva para a
 *     pagina da experiencia. Falar/Reservar vivem so na detail page.
 *   - Visual: fundo branco, borda verde discreta (token --success), sombra
 *     leve, hover lift + leve zoom da imagem.
 */
export function ExperienceCard({ exp }: ExperienceCardProps) {
  const tc = tierClass(exp.experienceTier);
  const cover = exp.images && exp.images.length > 0 ? exp.images[0] : null;
  // Link pela slug (URL canonica indexada no sitemap), com fallback pro code.
  // DetailRoute resolve tanto slug quanto code, entao continua abrindo a pagina;
  // usar a slug evita que o Google descubra uma URL /{code} divergente da canonica.
  const bookingUrl = `/${encodeURIComponent(exp.slug || exp.experienceCode)}`;
  const cityName = exp.location.split(',').pop()?.trim();
  const isInstant = exp.bookingType === 'Confirmação Imediata';
  const pillLabel = isInstant ? 'Confirmação Imediata' : 'Resposta em até 24h';
  const audienceText = formatAudience(exp.tags);
  const hasPrice = exp.price > 0;

  const handleCardClick = () =>
    trackEvent('experience_card_click', {
      experienceCode: exp.experienceCode,
      experienceName: exp.title,
      bookingType: exp.bookingType,
      city: exp.location,
      price: exp.price,
      source: 'card',
    });

  return (
    <article className="card exp-card">
      <a
        className="exp-card__link"
        href={bookingUrl}
        onClick={handleCardClick}
        aria-label={exp.title}
      />

      {cover && (
        <div className="card-media">
          <img src={cover} alt={exp.title} loading="lazy" decoding="async" />

          {exp.experienceTier && tc && (
            <div className="exp-tier-corner">
              <span className={`exp-tier-badge exp-tier-badge--${tc}`}>
                <TierIcon tier={tc} />
                {exp.experienceTier}
              </span>
            </div>
          )}

          <div className="exp-booking-corner">
            <span className={`booking-type-badge booking-type-badge--overlay booking-type-badge--${isInstant ? 'instant' : 'request'}`}>
              <span className="booking-type-badge__dot" aria-hidden="true" />
              <span className="booking-type-badge__label">{pillLabel}</span>
            </span>
          </div>
        </div>
      )}

      <div className="card-body">
        {cityName && (
          <span className="exp-city-label">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {cityName}
          </span>
        )}

        <h3 className="exp-title">{exp.title}</h3>

        {exp.summary && <p className="exp-summary">{exp.summary}</p>}

        <div className="exp-quick-info">
          {exp.duration && (
            <span className="exp-quick-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <polyline points="12 6 12 12 16 14" />
              </svg>
              {formatDuration(exp.duration)}
            </span>
          )}
          {exp.distanceKm != null && exp.distanceKm > 0 && (
            <span className="exp-quick-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polygon points="3 11 22 2 13 21 11 13 3 11" />
              </svg>
              {exp.distanceKm.toFixed(1)} km
            </span>
          )}
          {exp.category && (
            <span className="exp-quick-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 2H2v10l9.29 9.29c.94.94 2.48.94 3.42 0l6.58-6.58c.94-.94.94-2.48 0-3.42L12 2z" />
                <path d="M7 7h.01" />
              </svg>
              {exp.category}
            </span>
          )}
        </div>

        {(hasPrice || audienceText) && (
          <div className={`exp-card-bottom${audienceText && hasPrice ? ' exp-card-bottom--split' : ''}`}>
            {audienceText && (
              <div className="exp-audience">
                <span className="exp-audience__icon">
                  <AudienceIcon />
                </span>
                <span className="exp-audience__text">{audienceText}</span>
              </div>
            )}
            {hasPrice && (
              <div className="exp-price-row">
                <span className="exp-price-label">A partir de</span>
                <span className="exp-price-value">
                  <span className="exp-price-currency">{exp.currency}</span>
                  <strong>{Math.floor(exp.price)}</strong>
                </span>
                {exp.priceUnit && (
                  <span className="exp-price-unit">por {cleanPriceUnit(exp.priceUnit)}</span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </article>
  );
}
